/**
 * ============================================================================
 * SEO COMPONENT - Dynamic Meta Tags
 * ============================================================================
 * 
 * Handles dynamic SEO meta tags, Open Graph, Twitter Cards, and Structured Data
 * Uses direct DOM manipulation for maximum compatibility
 */

import { useEffect } from 'react';

/**
 * SEO Component
 * @param {Object} props
 * @param {string} props.title - Page title
 * @param {string} props.description - Meta description
 * @param {string} props.keywords - Meta keywords (comma-separated)
 * @param {string} props.image - OG/Twitter image URL
 * @param {string} props.url - Canonical URL
 * @param {string} props.type - OG type (website, article, etc.)
 * @param {Object} props.structuredData - JSON-LD structured data
 * @param {boolean} props.noindex - Set to true to prevent indexing
 * @param {string} props.locale - Language locale (en, fr)
 * @returns {null}
 */
const SEO = ({
  title = 'WekaTrust - Secure Escrow Platform for Cameroon',
  description = 'Secure peer-to-peer transactions with intelligent escrow protection. Mobile-first escrow platform optimized for Cameroon.',
  keywords = 'escrow, Cameroon, mobile money, MTN MoMo, Orange Money, secure payments, peer-to-peer, digital commerce, WekaTrust',
  image = 'https://wekatrust.com/og-image.jpg',
  url = 'https://wekatrust.com',
  type = 'website',
  structuredData = null,
  noindex = false,
  locale = 'en',
}) => {
  const siteName = 'WekaTrust';
  const siteUrl = 'https://wekatrust.com';
  const twitterHandle = '@wekatrust';
  
  // Ensure absolute URL for image
  const ogImage = image.startsWith('http') ? image : `${siteUrl}${image}`;
  const canonicalUrl = url.startsWith('http') ? url : `${siteUrl}${url}`;
  
  // Full title with site name
  const fullTitle = title.includes(siteName) ? title : `${title} | ${siteName}`;
  
  useEffect(() => {
    // Update document title
    document.title = fullTitle;
    
    // Helper function to update or create meta tag
    const updateMetaTag = (property, content, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attribute}="${property}"]`);
      
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, property);
        document.head.appendChild(meta);
      }
      
      meta.setAttribute('content', content);
    };
    
    // Helper function to update or create link tag
    const updateLinkTag = (rel, href) => {
      let link = document.querySelector(`link[rel="${rel}"]`);
      
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', rel);
        document.head.appendChild(link);
      }
      
      link.setAttribute('href', href);
    };
    
    // Primary Meta Tags
    updateMetaTag('title', fullTitle);
    updateMetaTag('description', description);
    if (keywords) {
      updateMetaTag('keywords', keywords);
    }
    updateMetaTag('robots', noindex ? 'noindex, nofollow' : 'index, follow');
    updateMetaTag('language', locale === 'fr' ? 'French' : 'English');
    
    // Canonical URL
    updateLinkTag('canonical', canonicalUrl);
    
    // Open Graph / Facebook
    updateMetaTag('og:type', type, true);
    updateMetaTag('og:url', canonicalUrl, true);
    updateMetaTag('og:title', fullTitle, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:image', ogImage, true);
    updateMetaTag('og:image:width', '1200', true);
    updateMetaTag('og:image:height', '630', true);
    updateMetaTag('og:image:alt', fullTitle, true);
    updateMetaTag('og:site_name', siteName, true);
    updateMetaTag('og:locale', locale === 'fr' ? 'fr_FR' : 'en_US', true);
    
    if (locale === 'fr') {
      updateMetaTag('og:locale:alternate', 'en_US', true);
    } else {
      updateMetaTag('og:locale:alternate', 'fr_FR', true);
    }
    
    // Twitter Card
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:url', canonicalUrl);
    updateMetaTag('twitter:title', fullTitle);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', ogImage);
    updateMetaTag('twitter:image:alt', fullTitle);
    updateMetaTag('twitter:creator', twitterHandle);
    updateMetaTag('twitter:site', twitterHandle);
    
    // Structured Data (JSON-LD)
    if (structuredData) {
      // Remove existing structured data script
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript) {
        existingScript.remove();
      }
      
      // Add new structured data script
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }
    
    // Cleanup function
    return () => {
      // Optionally clean up on unmount if needed
    };
  }, [fullTitle, description, keywords, ogImage, canonicalUrl, type, structuredData, noindex, locale, siteName, twitterHandle]);
  
  // This component doesn't render anything
  return null;
};

export default SEO;
